import apiService from './api.service';
import type { Offer } from '@/types/offer.types';
import { Status } from '@/types/common.types';
import { unwrapApiResponse } from '@/utils/apiResponse';

// This is the raw data structure received from the backend API
interface OfferDTO {
  id: string;
  title: string;
  description: string;
  discountPercentage?: number;
  discountAmount?: number;
  validFrom: string; // Comes as ISO string
  validUntil: string; // Comes as ISO string
  promoCode?: string;
  usageLimit?: number;
  usageCount?: number;
  status: 'active' | 'inactive'; // Backend might use lowercase
}


// Define the structure for creating/updating an offer from the frontend
export interface OfferFormData {
  id?: string;
  title: string;
  description: string;
  discountPercentage?: number;
  discount_amount?: number;
  promoCode: string;
  validFrom: string;
  validUntil: string;
  usage_limit?: number;
}

class OfferService {
  private mapDtoToOffer(dto: OfferDTO): Offer {
    return {
      ...dto,
  validFrom: new Date(dto.validFrom).toLocaleDateString(),
  validUntil: new Date(dto.validUntil).toLocaleDateString(),
  // prefer imageUrl returned by backend (cloudinary path). Fallbacks kept for older mock shape.
  imageUrl: (dto as any).imageUrl || (dto as any).imageName || undefined,
      status: dto.status === 'active' ? Status.ACTIVE : Status.INACTIVE,
    };
  }

  async getOffers(includeDeleted = false): Promise<Offer[]> {
    const response = await apiService.get<OfferDTO[]>('/offers', {
      params: { includeDeleted },
    });
    return unwrapApiResponse(response).map(this.mapDtoToOffer);
  }

  async getActiveOffers(): Promise<Offer[]> {
    const response = await apiService.get<OfferDTO[]>('/offers/active');
    return unwrapApiResponse(response).map(this.mapDtoToOffer);
  }

  async createOffer(offerData: OfferFormData | FormData): Promise<Offer> {
    const response = await apiService.post<OfferDTO>('/offers', offerData as any);
    return this.mapDtoToOffer(unwrapApiResponse(response));
  }

  async updateOffer(id: string, offerData: OfferFormData | FormData): Promise<Offer> {
    const response = await apiService.put<OfferDTO>(`/offers/${id}`, offerData as any);
    return this.mapDtoToOffer(unwrapApiResponse(response));
  }

  async deleteOffer(id: string): Promise<void> {
    await apiService.delete(`/offers/${id}`);
  }

  async restoreOffer(id: string): Promise<Offer> {
    const response = await apiService.post<OfferDTO>(`/offers/restore/${id}`);
    return this.mapDtoToOffer(unwrapApiResponse(response));
  }
}

const offerService = new OfferService();
export default offerService;
